// src/features/coupon/pages/AdminCouponPage.tsx

import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Gift,
  Plus,
  RefreshCw,
  Sparkles,
  Store,
  Ticket,
} from 'lucide-react';

import {
  useSelector,
} from 'react-redux';

import type {
  RootState,
} from '../../../app/store';

import {
  adminCouponService,
} from '../services/adminCouponService';

import {
  storeService,
} from '../../store/services/storeService';

import CouponCreateModal
  from '../components/CouponCreateModal';

import type {
  Coupon,
} from '../types/coupon.admin.types';

import type {
  CouponRewardType,
} from '../types/coupon.types';


type CouponFilter =
  | 'ALL'
  | 'ACTIVE'
  | 'INACTIVE'
  | 'FIXED'
  | 'RANDOM';


const FILTERS: {
  value: CouponFilter;
  label: string;
}[] = [
  {
    value: 'ALL',
    label: '전체',
  },
  {
    value: 'ACTIVE',
    label: '활성',
  },
  {
    value: 'INACTIVE',
    label: '비활성',
  },
  {
    value: 'FIXED',
    label: '고정 보상',
  },
  {
    value: 'RANDOM',
    label: '확률 보상',
  },
];


const formatNumber = (
  value: number | null,
): string => {

  if (
    value == null
  ) {
    return '-';
  }


  return value.toLocaleString(
    'ko-KR',
  );
};


const formatDate = (
  value: string | null,
): string => {

  if (!value) {
    return '-';
  }


  const date =
    new Date(
      value,
    );


  if (
    Number.isNaN(
      date.getTime(),
    )
  ) {
    return value;
  }


  return new Intl.DateTimeFormat(
    'ko-KR',
    {
      year:
        'numeric',

      month:
        '2-digit',

      day:
        '2-digit',
    },
  ).format(
    date,
  );
};


const getRewardLabel = (
  rewardType:
    CouponRewardType | null,

  rewardAmount:
    number | null,
): string => {

  if (!rewardType) {
    return '-';
  }


  switch (
    rewardType
  ) {

    case 'POINT':
      return `${formatNumber(
        rewardAmount,
      )}LS`;


    case 'STORE_POINT':
      return `${formatNumber(
        rewardAmount,
      )} 매장P`;


    case 'FREE_GAME':
      return `무료게임 ${formatNumber(
        rewardAmount,
      )}회`;


    case 'SATELLITE':
      return `새틀라이트 ${formatNumber(
        rewardAmount,
      )}회`;


    case 'SEAT':
      return `시트권 ${formatNumber(
        rewardAmount,
      )}매`;


    case 'ETC':
      return `기타 보상 ${formatNumber(
        rewardAmount,
      )}`;


    default:
      return '-';
  }
};


export default function AdminCouponPage():
  React.JSX.Element {

  const authUser =
    useSelector(
      (
        state:
          RootState,
      ) =>
        state.auth.user,
    );


  const [
    coupons,
    setCoupons,
  ] =
    useState<
      Coupon[]
    >([]);


  const [
    stores,
    setStores,
  ] =
    useState<
      {
        storeId: number;
        storeName: string;
      }[]
    >([]);


  const [
    storeNameMap,
    setStoreNameMap,
  ] =
    useState<
      Record<
        number,
        string
      >
    >({});


  /**
   * =========================================================
   * SUPERADMIN 매장 필터
   *
   * 0
   * -> 전체 매장
   * =========================================================
   */
  const [
    selectedStoreId,
    setSelectedStoreId,
  ] =
    useState(
      0,
    );


  const [
    filter,
    setFilter,
  ] =
    useState<CouponFilter>(
      'ALL',
    );


  const [
    loading,
    setLoading,
  ] =
    useState(
      true,
    );


  const [
    errorMessage,
    setErrorMessage,
  ] =
    useState(
      '',
    );


  const [
    processingCouponId,
    setProcessingCouponId,
  ] =
    useState<
      number | null
    >(
      null,
    );


  const [
    createModalOpen,
    setCreateModalOpen,
  ] =
    useState(
      false,
    );


  /**
   * =========================================================
   * 현재 관리자 권한
   * =========================================================
   */
  const currentRole =
    authUser?.role ===
      'SUPERADMIN'
      ? 'SUPERADMIN'
      : 'ADMIN';


  const isSuperAdmin =
    currentRole ===
    'SUPERADMIN';


  const currentStoreId =
    authUser?.storeId ??
    null;


  /**
   * =========================================================
   * 쿠폰 목록 조회
   * =========================================================
   */
  const loadCoupons =
    useCallback(
      async (): Promise<void> => {

        try {

          setLoading(
            true,
          );


          setErrorMessage(
            '',
          );


          const data =
            await adminCouponService
              .getCoupons();


          setCoupons(
            Array.isArray(
              data,
            )
              ? data
              : [],
          );

        } catch (
          error
        ) {

          console.error(
            '관리자 쿠폰 조회 실패:',
            error,
          );


          setErrorMessage(
            '쿠폰 목록을 불러오지 못했습니다.',
          );

        } finally {

          setLoading(
            false,
          );

        }
      },
      [],
    );


  /**
   * =========================================================
   * 매장 목록 조회
   * =========================================================
   */
  const loadStores =
    useCallback(
      async (): Promise<void> => {

        try {

          const data =
            await storeService
              .getStores();


          const safeStores =
            Array.isArray(
              data,
            )
              ? data
              : [];


          setStores(
            safeStores,
          );


          const map:
            Record<
              number,
              string
            > =
              {};


          safeStores.forEach(
            (
              store,
            ) => {

              map[
                store.storeId
              ] =
                store.storeName;

            },
          );


          setStoreNameMap(
            map,
          );

        } catch (
          error
        ) {

          console.error(
            '매장 조회 실패:',
            error,
          );

        }
      },
      [],
    );


  /**
   * =========================================================
   * 최초 로딩
   * =========================================================
   */
  useEffect(
    () => {

      void Promise.all([
        loadCoupons(),
        loadStores(),
      ]);

    },
    [
      loadCoupons,
      loadStores,
    ],
  );


  /**
   * =========================================================
   * 쿠폰 생성 완료
   * =========================================================
   */
  const handleCreated = (
    created:
      Coupon,
  ): void => {

    setCoupons(
      (
        previous,
      ) => [
        created,
        ...previous,
      ],
    );
  };


  /**
   * =========================================================
   * 쿠폰 활성 / 비활성
   * =========================================================
   */
  const handleToggleActive =
    async (
      coupon:
        Coupon,
    ): Promise<void> => {

      if (
        processingCouponId !=
        null
      ) {
        return;
      }


      const confirmed =
        window.confirm(
          coupon.active
            ? `"${coupon.couponName}" 쿠폰을 비활성화하시겠습니까?`
            : `"${coupon.couponName}" 쿠폰을 활성화하시겠습니까?`,
        );


      if (
        !confirmed
      ) {
        return;
      }


      try {

        setProcessingCouponId(
          coupon.couponId,
        );


        const updated =
          coupon.active
            ? await adminCouponService
                .deactivateCoupon(
                  coupon.couponId,
                )
            : await adminCouponService
                .activateCoupon(
                  coupon.couponId,
                );


        setCoupons(
          (
            previous,
          ) =>
            previous.map(
              (
                item,
              ) =>
                item.couponId ===
                  updated.couponId
                  ? updated
                  : item,
            ),
        );

      } catch (
        error
      ) {

        console.error(
          '쿠폰 상태 변경 실패:',
          error,
        );


        window.alert(
          '쿠폰 상태 변경에 실패했습니다.',
        );

      } finally {

        setProcessingCouponId(
          null,
        );

      }
    };


  /**
   * =========================================================
   * 매장 기준 1차 필터링
   *
   * SUPERADMIN
   * -> 선택한 매장 기준
   *
   * ADMIN
   * -> 서버에서 전달된 목록 그대로
   * =========================================================
   */
  const storeFilteredCoupons =
    useMemo(
      () => {

        if (
          !isSuperAdmin
        ) {
          return coupons;
        }


        if (
          selectedStoreId ===
          0
        ) {
          return coupons;
        }


        return coupons.filter(
          (
            coupon,
          ) =>
            coupon.storeId ===
            selectedStoreId,
        );

      },
      [
        coupons,
        isSuperAdmin,
        selectedStoreId,
      ],
    );


  /**
   * =========================================================
   * 상태 / 보상 방식 필터 + 정렬
   *
   * 1. 매장 필터
   * 2. 상태/보상 필터
   * 3. 활성 쿠폰 우선
   * 4. 비활성 쿠폰 후순위
   * 5. 동일 상태 최신순
   * =========================================================
   */
  const filteredCoupons =
    useMemo(
      () => {

        let result:
          Coupon[];


        switch (
          filter
        ) {

          case 'ACTIVE':

            result =
              storeFilteredCoupons.filter(
                (
                  coupon,
                ) =>
                  coupon.active,
              );

            break;


          case 'INACTIVE':

            result =
              storeFilteredCoupons.filter(
                (
                  coupon,
                ) =>
                  !coupon.active,
              );

            break;


          case 'FIXED':

            result =
              storeFilteredCoupons.filter(
                (
                  coupon,
                ) =>
                  coupon.rewardMode ===
                  'FIXED',
              );

            break;


          case 'RANDOM':

            result =
              storeFilteredCoupons.filter(
                (
                  coupon,
                ) =>
                  coupon.rewardMode ===
                  'RANDOM',
              );

            break;


          default:

            result =
              [
                ...storeFilteredCoupons,
              ];

            break;
        }


        return [
          ...result,
        ].sort(
          (
            first,
            second,
          ) => {

            /**
             * 활성 우선
             */
            if (
              first.active !==
              second.active
            ) {

              return first.active
                ? -1
                : 1;
            }


            /**
             * 동일 상태는 최신순
             */
            const firstTime =
              first.createdAt
                ? new Date(
                    first.createdAt,
                  ).getTime()
                : 0;


            const secondTime =
              second.createdAt
                ? new Date(
                    second.createdAt,
                  ).getTime()
                : 0;


            return (
              secondTime -
              firstTime
            );
          },
        );

      },
      [
        storeFilteredCoupons,
        filter,
      ],
    );


  /**
   * =========================================================
   * 통계
   *
   * SUPERADMIN이 매장을 선택하면
   * 선택 매장 기준으로 통계 계산
   * =========================================================
   */
  const activeCount =
    useMemo(
      () =>
        storeFilteredCoupons.filter(
          (
            coupon,
          ) =>
            coupon.active,
        ).length,
      [
        storeFilteredCoupons,
      ],
    );


  const inactiveCount =
    useMemo(
      () =>
        storeFilteredCoupons.filter(
          (
            coupon,
          ) =>
            !coupon.active,
        ).length,
      [
        storeFilteredCoupons,
      ],
    );


  const fixedCount =
    useMemo(
      () =>
        storeFilteredCoupons.filter(
          (
            coupon,
          ) =>
            coupon.rewardMode ===
            'FIXED',
        ).length,
      [
        storeFilteredCoupons,
      ],
    );


  const randomCount =
    useMemo(
      () =>
        storeFilteredCoupons.filter(
          (
            coupon,
          ) =>
            coupon.rewardMode ===
            'RANDOM',
        ).length,
      [
        storeFilteredCoupons,
      ],
    );


  return (
    <main
      className="
        min-h-screen
        bg-gray-50
        px-4
        py-5
        sm:px-6
        sm:py-7
        lg:px-8
      "
    >

      <div
        className="
          mx-auto
          w-full
          max-w-6xl
        "
      >

        {/* =====================================================
            Header
            ===================================================== */}
        <header
          className="
            mb-5
            flex
            flex-col
            gap-4
            sm:flex-row
            sm:items-end
            sm:justify-between
          "
        >

          <div>

            <p
              className="
                text-xs
                font-bold
                tracking-[0.18em]
                text-main
              "
            >
              COUPON MANAGEMENT
            </p>


            <h1
              className="
                mt-1
                text-2xl
                font-bold
                text-gray-900
                sm:text-3xl
              "
            >
              쿠폰 관리
            </h1>


            <p
              className="
                mt-1
                text-sm
                text-gray-500
              "
            >
              쿠폰을 생성하고 활성 상태 및 보상 정책을 관리합니다.
            </p>

          </div>


          <div
            className="
              flex
              flex-wrap
              items-center
              gap-2
            "
          >

            <button
              type="button"
              disabled={
                loading
              }
              onClick={() =>
                void Promise.all([
                  loadCoupons(),
                  loadStores(),
                ])
              }
              className="
                inline-flex
                h-10
                items-center
                justify-center
                gap-2
                rounded-lg
                border
                border-gray-200
                bg-white
                px-3
                text-sm
                font-semibold
                text-gray-600
                transition
                hover:bg-gray-50
                disabled:opacity-50
              "
            >
              <RefreshCw
                size={15}
                className={
                  loading
                    ? 'animate-spin'
                    : ''
                }
              />

              새로고침
            </button>


            <button
              type="button"
              onClick={() =>
                setCreateModalOpen(
                  true,
                )
              }
              className="
                inline-flex
                h-10
                items-center
                justify-center
                gap-1.5
                rounded-lg
                bg-main
                px-4
                text-sm
                font-bold
                text-white
                transition
                hover:opacity-90
              "
            >
              <Plus
                size={16}
              />

              쿠폰 생성
            </button>

          </div>

        </header>


        {/* =====================================================
            SUPERADMIN 매장 선택
            ===================================================== */}
        {isSuperAdmin && (

          <section
            className="
              mb-4
              rounded-xl
              border
              border-gray-200
              bg-white
              px-4
              py-3
              shadow-sm
            "
          >

            <div
              className="
                flex
                flex-col
                gap-2
                sm:flex-row
                sm:items-center
              "
            >

              <div
                className="
                  flex
                  items-center
                  gap-2
                  sm:w-36
                "
              >

                <Store
                  size={17}
                  className="
                    text-gray-500
                  "
                />


                <label
                  htmlFor="coupon-store-filter"
                  className="
                    text-sm
                    font-bold
                    text-gray-700
                  "
                >
                  매장 선택
                </label>

              </div>


              <select
                id="coupon-store-filter"
                value={
                  selectedStoreId
                }
                onChange={(
                  event,
                ) =>
                  setSelectedStoreId(
                    Number(
                      event.target.value,
                    ),
                  )
                }
                className="
                  h-10
                  w-full
                  rounded-lg
                  border
                  border-gray-300
                  bg-white
                  px-3
                  text-sm
                  font-medium
                  text-gray-700
                  outline-none
                  transition
                  focus:border-main
                  focus:ring-2
                  focus:ring-main/10
                  sm:max-w-sm
                "
              >

                <option
                  value={0}
                >
                  전체 매장
                </option>


                {stores.map(
                  (
                    store,
                  ) => (

                    <option
                      key={
                        store.storeId
                      }
                      value={
                        store.storeId
                      }
                    >
                      {store.storeName}
                    </option>

                  ),
                )}

              </select>


              {selectedStoreId !==
                0 && (

                <span
                  className="
                    text-xs
                    text-gray-400
                  "
                >
                  {
                    storeNameMap[
                      selectedStoreId
                    ] ??
                    `매장 #${selectedStoreId}`
                  } 쿠폰만 표시 중
                </span>

              )}

            </div>

          </section>

        )}


        {/* =====================================================
            Summary
            ===================================================== */}
        <section
          className="
            mb-5
            grid
            grid-cols-2
            gap-2
            sm:grid-cols-4
          "
        >

          <div
            className="
              rounded-xl
              border
              border-gray-200
              bg-white
              px-4
              py-3
              shadow-sm
            "
          >

            <p
              className="
                text-xs
                font-semibold
                text-gray-400
              "
            >
              전체 쿠폰
            </p>


            <p
              className="
                mt-1
                text-xl
                font-bold
                text-gray-900
              "
            >
              {storeFilteredCoupons.length}

              <span
                className="
                  ml-1
                  text-xs
                  font-medium
                  text-gray-400
                "
              >
                개
              </span>
            </p>

          </div>


          <div
            className="
              rounded-xl
              border
              border-gray-200
              bg-white
              px-4
              py-3
              shadow-sm
            "
          >

            <p
              className="
                text-xs
                font-semibold
                text-gray-400
              "
            >
              활성
            </p>


            <p
              className="
                mt-1
                text-xl
                font-bold
                text-emerald-600
              "
            >
              {activeCount}

              <span
                className="
                  ml-1
                  text-xs
                  font-medium
                  text-gray-400
                "
              >
                개
              </span>
            </p>

          </div>


          <div
            className="
              rounded-xl
              border
              border-gray-200
              bg-white
              px-4
              py-3
              shadow-sm
            "
          >

            <p
              className="
                text-xs
                font-semibold
                text-gray-400
              "
            >
              고정 보상
            </p>


            <p
              className="
                mt-1
                text-xl
                font-bold
                text-gray-900
              "
            >
              {fixedCount}

              <span
                className="
                  ml-1
                  text-xs
                  font-medium
                  text-gray-400
                "
              >
                개
              </span>
            </p>

          </div>


          <div
            className="
              rounded-xl
              border
              border-gray-200
              bg-white
              px-4
              py-3
              shadow-sm
            "
          >

            <p
              className="
                text-xs
                font-semibold
                text-gray-400
              "
            >
              확률 보상
            </p>


            <p
              className="
                mt-1
                text-xl
                font-bold
                text-violet-600
              "
            >
              {randomCount}

              <span
                className="
                  ml-1
                  text-xs
                  font-medium
                  text-gray-400
                "
              >
                개
              </span>
            </p>

          </div>

        </section>


        {/* =====================================================
            Filter
            ===================================================== */}
        <section
          className="
            mb-4
            overflow-x-auto
            pb-1
          "
        >

          <div
            className="
              flex
              min-w-max
              gap-2
            "
          >

            {FILTERS.map(
              (
                item,
              ) => {

                const selected =
                  filter ===
                  item.value;


                let count =
                  storeFilteredCoupons.length;


                if (
                  item.value ===
                  'ACTIVE'
                ) {
                  count =
                    activeCount;
                }


                if (
                  item.value ===
                  'INACTIVE'
                ) {
                  count =
                    inactiveCount;
                }


                if (
                  item.value ===
                  'FIXED'
                ) {
                  count =
                    fixedCount;
                }


                if (
                  item.value ===
                  'RANDOM'
                ) {
                  count =
                    randomCount;
                }


                return (
                  <button
                    key={
                      item.value
                    }
                    type="button"
                    onClick={() =>
                      setFilter(
                        item.value,
                      )
                    }
                    className={`
                      rounded-full
                      border
                      px-3.5
                      py-1.5
                      text-xs
                      font-semibold
                      transition

                      ${
                        selected
                          ? `
                            border-main
                            bg-main
                            text-white
                          `
                          : `
                            border-gray-200
                            bg-white
                            text-gray-600
                            hover:border-main
                            hover:text-main
                          `
                      }
                    `}
                  >
                    {item.label}

                    <span
                      className={`
                        ml-1.5

                        ${
                          selected
                            ? 'text-white/70'
                            : 'text-gray-400'
                        }
                      `}
                    >
                      {count}
                    </span>
                  </button>
                );
              },
            )}

          </div>

        </section>


        {/* =====================================================
            Content
            ===================================================== */}
        {loading ? (

          <div
            className="
              flex
              min-h-[300px]
              items-center
              justify-center
            "
          >
            <span
              className="
                h-9
                w-9
                animate-spin
                rounded-full
                border-4
                border-gray-200
                border-t-main
              "
            />
          </div>

        ) : errorMessage ? (

          <div
            className="
              rounded-2xl
              border
              border-red-100
              bg-white
              px-5
              py-10
              text-center
            "
          >

            <p
              className="
                text-sm
                text-red-500
              "
            >
              {errorMessage}
            </p>


            <button
              type="button"
              onClick={() =>
                void loadCoupons()
              }
              className="
                mt-4
                rounded-lg
                bg-main
                px-4
                py-2
                text-sm
                font-bold
                text-white
              "
            >
              다시 시도
            </button>

          </div>

        ) : filteredCoupons.length ===
          0 ? (

          <div
            className="
              rounded-2xl
              border
              border-gray-200
              bg-white
              px-5
              py-16
              text-center
            "
          >

            <Ticket
              size={36}
              className="
                mx-auto
                text-gray-300
              "
            />


            <p
              className="
                mt-3
                font-semibold
                text-gray-700
              "
            >
              표시할 쿠폰이 없습니다.
            </p>


            <p
              className="
                mt-1
                text-sm
                text-gray-400
              "
            >
              매장 또는 필터를 변경해주세요.
            </p>

          </div>

        ) : (

          <section
            className="
              grid
              grid-cols-1
              gap-3
              lg:grid-cols-2
            "
          >

            {filteredCoupons.map(
              (
                coupon,
              ) => {

                const isRandom =
                  coupon.rewardMode ===
                  'RANDOM';


                const storeName =
                  coupon.storeId ==
                    null
                    ? '전체 / 공용'
                    : (
                        storeNameMap[
                          coupon.storeId
                        ] ??
                        `매장 #${coupon.storeId}`
                      );


                const processing =
                  processingCouponId ===
                  coupon.couponId;


                return (
                  <article
                    key={
                      coupon.couponId
                    }
                    className={`
                      overflow-hidden
                      rounded-xl
                      border
                      bg-white
                      shadow-sm
                      transition

                      ${
                        coupon.active
                          ? `
                            border-gray-200
                            hover:border-gray-300
                          `
                          : `
                            border-gray-200
                            opacity-70
                          `
                      }
                    `}
                  >

                    {/* Card Header */}
                    <div
                      className="
                        flex
                        items-start
                        justify-between
                        gap-3
                        px-4
                        py-3.5
                      "
                    >

                      <div
                        className="
                          min-w-0
                          flex-1
                        "
                      >

                        <div
                          className="
                            flex
                            items-center
                            gap-1.5
                          "
                        >

                          {isRandom ? (

                            <Sparkles
                              size={16}
                              className="
                                shrink-0
                                text-violet-600
                              "
                            />

                          ) : (

                            <Ticket
                              size={16}
                              className="
                                shrink-0
                                text-main
                              "
                            />

                          )}


                          <h2
                            className="
                              truncate
                              text-[15px]
                              font-bold
                              text-gray-900
                            "
                          >
                            {coupon.couponName}
                          </h2>

                        </div>


                        {coupon.description && (
                          <p
                            className="
                              mt-1
                              line-clamp-2
                              text-xs
                              leading-5
                              text-gray-500
                            "
                          >
                            {coupon.description}
                          </p>
                        )}

                      </div>


                      <div
                        className="
                          flex
                          shrink-0
                          items-center
                          gap-1.5
                        "
                      >

                        <span
                          className={`
                            rounded-full
                            px-2
                            py-0.5
                            text-[10px]
                            font-bold

                            ${
                              isRandom
                                ? `
                                  bg-violet-50
                                  text-violet-700
                                `
                                : `
                                  bg-blue-50
                                  text-blue-700
                                `
                            }
                          `}
                        >
                          {isRandom
                            ? 'RANDOM'
                            : 'FIXED'}
                        </span>


                        <span
                          className={`
                            rounded-full
                            px-2
                            py-0.5
                            text-[10px]
                            font-bold

                            ${
                              coupon.active
                                ? `
                                  bg-emerald-50
                                  text-emerald-700
                                `
                                : `
                                  bg-gray-100
                                  text-gray-500
                                `
                            }
                          `}
                        >
                          {coupon.active
                            ? '활성'
                            : '비활성'}
                        </span>

                      </div>

                    </div>


                    {/* Reward */}
                    <div
                      className="
                        border-t
                        border-gray-100
                        px-4
                        py-3
                      "
                    >

                      {isRandom ? (

                        <div>

                          <div
                            className="
                              flex
                              items-center
                              justify-between
                              gap-2
                            "
                          >

                            <div
                              className="
                                flex
                                items-center
                                gap-2
                              "
                            >
                              <Gift
                                size={15}
                                className="
                                  text-violet-600
                                "
                              />

                              <span
                                className="
                                  text-xs
                                  font-bold
                                  text-gray-800
                                "
                              >
                                확률 보상
                              </span>
                            </div>


                            <span
                              className="
                                text-[10px]
                                text-gray-400
                              "
                            >
                              {
                                coupon
                                  .rewardOptions
                                  ?.length ??
                                0
                              }개 옵션
                            </span>

                          </div>


                          <div
                            className="
                              mt-2.5
                              space-y-1.5
                            "
                          >

                            {coupon.rewardOptions
                              ?.map(
                                (
                                  option,
                                  index,
                                ) => {

                                  const probability =
                                    option
                                      .probabilityWeight /
                                    100;


                                  return (
                                    <div
                                      key={
                                        option
                                          .rewardOptionId ??
                                        index
                                      }
                                      className="
                                        flex
                                        items-center
                                        justify-between
                                        gap-3
                                        rounded-lg
                                        bg-gray-50
                                        px-3
                                        py-2
                                      "
                                    >

                                      <span
                                        className="
                                          truncate
                                          text-xs
                                          font-medium
                                          text-gray-700
                                        "
                                      >
                                        {getRewardLabel(
                                          option
                                            .rewardType,

                                          option
                                            .rewardAmount,
                                        )}
                                      </span>


                                      <span
                                        className="
                                          shrink-0
                                          text-xs
                                          font-bold
                                          text-violet-600
                                        "
                                      >
                                        {probability.toFixed(
                                          2,
                                        )}
                                        %
                                      </span>

                                    </div>
                                  );
                                },
                              )}

                          </div>

                        </div>

                      ) : (

                        <div
                          className="
                            flex
                            items-center
                            gap-2
                          "
                        >

                          <Gift
                            size={15}
                            className="
                              text-main
                            "
                          />


                          <div>

                            <p
                              className="
                                text-[10px]
                                font-semibold
                                text-gray-400
                              "
                            >
                              고정 보상
                            </p>


                            <span
                              className="
                                text-sm
                                font-bold
                                text-gray-900
                              "
                            >
                              {getRewardLabel(
                                coupon.rewardType,
                                coupon.rewardAmount,
                              )}
                            </span>

                          </div>

                        </div>

                      )}


                      {/* Meta */}
                      <div
                        className="
                          mt-3
                          grid
                          gap-1.5
                          border-t
                          border-gray-100
                          pt-2.5
                          text-[11px]
                          text-gray-500
                          sm:grid-cols-3
                        "
                      >

                        <div
                          className="
                            flex
                            min-w-0
                            items-center
                            gap-1
                          "
                        >

                          <Store
                            size={12}
                            className="
                              shrink-0
                            "
                          />


                          <span
                            className="
                              truncate
                            "
                          >
                            {storeName}
                          </span>

                        </div>


                        <div>
                          발급 후{' '}

                          <strong
                            className="
                              font-semibold
                              text-gray-700
                            "
                          >
                            {coupon.validDays}일
                          </strong>
                        </div>


                        <div>
                          생성{' '}

                          {formatDate(
                            coupon.createdAt,
                          )}
                        </div>

                      </div>

                    </div>


                    {/* Footer */}
                    <div
                      className="
                        flex
                        items-center
                        justify-between
                        gap-3
                        border-t
                        border-gray-100
                        bg-gray-50/50
                        px-3
                        py-2.5
                      "
                    >

                      <div>

                        <span
                          className="
                            text-[10px]
                            text-gray-400
                          "
                        >
                          쿠폰 ID #{coupon.couponId}
                        </span>


                        <p
                          className="
                            mt-0.5
                            text-[10px]
                            font-medium
                            text-gray-500
                          "
                        >
                          현재 상태:{' '}

                          <span
                            className={
                              coupon.active
                                ? 'text-emerald-600'
                                : 'text-gray-500'
                            }
                          >
                            {coupon.active
                              ? '활성'
                              : '비활성'}
                          </span>
                        </p>

                      </div>


                      <button
                        type="button"
                        disabled={
                          processing
                        }
                        onClick={() =>
                          void handleToggleActive(
                            coupon,
                          )
                        }
                        className={`
                          h-8
                          shrink-0
                          rounded-lg
                          border
                          px-3
                          text-xs
                          font-semibold
                          transition
                          disabled:cursor-not-allowed
                          disabled:opacity-50

                          ${
                            coupon.active
                              ? `
                                border-red-200
                                bg-white
                                text-red-500
                                hover:bg-red-50
                              `
                              : `
                                border-main
                                bg-white
                                text-main
                                hover:bg-main/5
                              `
                          }
                        `}
                      >
                        {processing
                          ? '처리 중...'
                          : coupon.active
                            ? '비활성화하기'
                            : '활성화하기'}
                      </button>

                    </div>

                  </article>
                );
              },
            )}

          </section>

        )}

      </div>


      {/* =====================================================
          Coupon Create Modal
          ===================================================== */}
      {createModalOpen && (
        <CouponCreateModal
          stores={
            stores
          }
          currentRole={
            currentRole
          }
          currentStoreId={
            currentStoreId
          }
          onClose={() =>
            setCreateModalOpen(
              false,
            )
          }
          onCreated={
            handleCreated
          }
        />
      )}

    </main>
  );
}